import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { useSubscription } from "@/hooks/useSubscription";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { PlansGrid } from "@/components/subscription/PlansGrid";
import { Card } from "@/components/ui/card";
import { AlertTriangle, Loader2, Sparkles } from "lucide-react";

export const Route = createFileRoute("/_app/plans")({
  component: PlansPage,
  head: () => ({
    meta: [
      { title: "Plans & Pricing — Business Dite Ads" },
      { name: "description", content: "Choose your Business Dite Ads plan." },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function PlansPage() {
  const { t } = useTranslation();
  const { loading: authLoading } = useAuth();
  const { hasAccess, loading: subLoading, tier, subscription } = useSubscription();

  if (authLoading || subLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PaymentTestModeBanner />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight sm:text-3xl">
            <Sparkles className="h-6 w-6 text-primary" />
            {t("subscription.upgrade.title")}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {t("subscription.upgrade.subtitleAfterTrial")}
          </p>
        </div>
        {tier && (
          <div className="rounded-full border border-primary/30 bg-primary/5 px-3 py-1 text-xs font-medium text-primary">
            {t("subscription.upgrade.currentPlan")}: {t(`pricing.plans.${tier}.name`)}
          </div>
        )}
      </div>

      {!hasAccess && (
        <Card className="border-destructive/40 bg-destructive/5 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-semibold">{t("subscription.upgrade.trialExpiredBadge")}</p>
              {subscription?.status === "past_due" && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("subscription.upgrade.pastDueBody")}
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      <PlansGrid />

      <p className="text-center text-xs text-muted-foreground">
        {t("subscription.upgrade.secure")}{" "}
        <a href="mailto:contact@diteads.com" className="underline">
          {t("subscription.upgrade.contactSupport")}
        </a>
        .
      </p>
    </div>
  );
}
